/**
 * Zustand auth store with persistence.
 *
 * Works in both web (localStorage) and React Native (AsyncStorage).
 */

import { create } from 'zustand';
import { persist, createJSONStorage, type StateStorage } from 'zustand/middleware';
import { setTokenGetter, setOnUnauthorized } from '../api/client';
import { authApi } from '../api/auth';
import { authPersistKey } from '../utils/env';
import type { User } from '../types';

/** Auth state interface */
interface AuthState {
  /** Current user */
  user: User | null;
  /** JWT token */
  token: string | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether auth is being checked */
  isLoading: boolean;
  /** Login with credentials */
  login: (email: string, password: string) => Promise<void>;
  /** Logout and clear state */
  logout: () => Promise<void>;
  /** Set token directly (for token refresh) */
  setToken: (token: string) => void;
  /** Check current auth status */
  checkAuth: () => Promise<void>;
  /** Clear auth state */
  clear: () => void;
}

/** Default web storage (localStorage) */
const webStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') return;
    localStorage.setItem(name, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(name);
  },
};

/**
 * Create auth store.
 *
 * @param storage - Optional storage adapter (default: localStorage)
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Set up token getter for API client
      setTokenGetter(() => get().token);

      // Clear auth state on any 401 response so the UI redirects to login
      setOnUnauthorized(() => {
        set({ user: null, token: null, isAuthenticated: false, isLoading: false });
      });

      return {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,

        login: async (email: string, password: string) => {
          set({ isLoading: true });
          try {
            const response = await authApi.login({ email, password });
            // AB#540: the /auth/login envelope is flat ({token,pid,name,is_verified})
            // and carries NO `user`/`email`, so `response.data?.user` is always
            // undefined — leaving `user` null for the whole SPA session (AuthGate's
            // checkAuth only runs at the initial page load, not after login). That
            // silently disabled the account-delete confirm button, which gates on
            // user.email. Set the token first, then hydrate the full user (with
            // email) from /auth/current via the existing checkAuth path.
            //
            // Do NOT flip isAuthenticated before checkAuth resolves: the login
            // route redirects the moment it flips, the app mounts /sites and its
            // queries start fetching, and THEN the user-pid hydration triggers
            // the stale-account cache clear (main.tsx) — which strands the
            // in-flight mounted queries in a forever-pending state (eternal
            // skeletons until refresh). checkAuth sets isAuthenticated + user
            // atomically, so the redirect now happens only after hydration.
            set({ token: response.data?.token ?? null });
            await get().checkAuth();
          } catch (error) {
            set({ isLoading: false, isAuthenticated: false });
            throw error;
          }
        },

        logout: async () => {
          try {
            await authApi.logout();
          } catch {
            // Ignore logout errors
          }
          set({
            user: null,
            token: null,
            isAuthenticated: false,
          });
        },

        setToken: (token: string) => {
          set({ token, isAuthenticated: true });
        },

        checkAuth: async () => {
          const { token } = get();
          if (!token) {
            set({ isAuthenticated: false, user: null });
            return;
          }

          set({ isLoading: true });
          try {
            const response = await authApi.me();
            set({
              user: (response.data as User | undefined) ?? null,
              isAuthenticated: true,
              isLoading: false,
            });
          } catch {
            // Token invalid or expired
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        },

        clear: () => {
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        },
      };
    },
    {
      // AB#425: per-env key (wpp-auth:qa etc) when launched from the desktop shell;
      // plain browser resolves to 'wpp-auth'. Resolved synchronously at module load.
      name: authPersistKey,
      storage: createJSONStorage(() => webStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

/**
 * Create auth store with custom storage (for React Native).
 *
 * @example
 * ```ts
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 *
 * const storage: StateStorage = {
 *   getItem: async (name) => await AsyncStorage.getItem(name),
 *   setItem: async (name, value) => await AsyncStorage.setItem(name, value),
 *   removeItem: async (name) => await AsyncStorage.removeItem(name),
 * };
 *
 * // Use in React Native app initialization
 * ```
 */
export function createAuthStoreWithStorage(storage: StateStorage) {
  return create<AuthState>()(
    persist(
      (set, get) => {
        setTokenGetter(() => get().token);

        return {
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          login: async (email, password) => {
            set({ isLoading: true });
            const response = await authApi.login({ email, password });
            set({
              user: response.data?.user ?? null,
              token: response.data?.token ?? null,
              isAuthenticated: true,
              isLoading: false,
            });
          },
          logout: async () => {
            try {
              await authApi.logout();
            } catch {
              // Ignore
            }
            set({ user: null, token: null, isAuthenticated: false });
          },
          setToken: (token) => set({ token, isAuthenticated: true }),
          checkAuth: async () => {
            const { token } = get();
            if (!token) {
              set({ isAuthenticated: false, user: null });
              return;
            }
            try {
              const response = await authApi.me();
              set({ user: (response.data as User | undefined) ?? null, isAuthenticated: true });
            } catch {
              set({ user: null, token: null, isAuthenticated: false });
            }
          },
          clear: () =>
            set({ user: null, token: null, isAuthenticated: false, isLoading: false }),
        };
      },
      {
        name: 'wpp-auth',
        storage: createJSONStorage(() => storage),
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
        }),
      },
    ),
  );
}
